import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, TrendingUp, BookOpen, AlertTriangle, CheckCircle, 
  Clock, Search, Filter, ChevronDown, ChevronUp, Eye,
  Upload, FileText, BarChart3, UserCheck, XCircle, Plus, Briefcase, Edit, Trash2,
  Settings, Key, Database, FolderOpen, Save, Loader2, BookMarked, Building2, DollarSign,
  Volume2, Play, Pause, Music, Mic, Globe, Link, FileSearch, Sparkles, RefreshCw,
  CreditCard, Package, Cloud, Power, Rocket, Bot, Cpu, Video, Mail, Tag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BookGenerator from '../components/BookGenerator';
import CreditsManagement from '../components/admin/CreditsManagement';
import PlansManagement from '../components/admin/PlansManagement';
import GoogleDriveManager from '../components/admin/GoogleDriveManager';
import CountriesManagement from '../components/admin/CountriesManagement';
import SystemSettingsPanel from '../components/admin/SystemSettingsPanel';
import ProfessionsManagement from '../components/admin/ProfessionsManagement';
import AIConfigPanel from '../components/admin/AIConfigPanel';
import ContentGenerationFlow from '../components/admin/ContentGenerationFlow';
import ContentManagement from '../components/admin/ContentManagement';
import LiveClassesManagement from '../components/admin/LiveClassesManagement';
import ContentAudit from '../components/admin/ContentAudit';
import CostCalculator from '../components/admin/CostCalculator';
import EmailCampaigns from '../components/admin/EmailCampaigns';
import InvoiceManager from '../components/admin/InvoiceManager';
import ProfessionPricing from '../components/admin/ProfessionPricing';
import LanguageSelector from '../components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get token from localStorage
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const AdminPanel = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfession, setFilterProfession] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAddProfession, setShowAddProfession] = useState(false);
  const [newProfession, setNewProfession] = useState({ name: '', description: '', category: '', tutor_system_prompt: '' });
  const [editingExamConfig, setEditingExamConfig] = useState(null);
  const [examConfig, setExamConfig] = useState({
    total_questions: 60,
    reserve_questions: 12,
    time_limit_minutes: 150,
    correct_points: 1.0,
    incorrect_points: -0.25,
    blank_points: 0,
    passing_score: 50,
    wrong_answers_to_deduct: 4
  });
  // Legal content management
  const [editingLegalContent, setEditingLegalContent] = useState(null);
  const [legalContent, setLegalContent] = useState('');
  const [savingLegal, setSavingLegal] = useState(false);
  // Contact leads
  const [leads, setLeads] = useState([]);
  const [institutionalLeads, setInstitutionalLeads] = useState([]);
  // API Keys management
  const [apiKeys, setApiKeys] = useState({
    openai_api_key: '',
    resend_api_key: '',
    stripe_api_key: '',
    elevenlabs_api_key: '',
    emergent_llm_key: '',
    salesprocess_api_key: '',
    salesprocess_workspace_id: '',
    salesprocess_webhook_url: ''
  });
  const [savingKeys, setSavingKeys] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  // Temario/Material management
  const [selectedProfessionForMaterial, setSelectedProfessionForMaterial] = useState('');
  const [materialFile, setMaterialFile] = useState(null);
  const [materialDescription, setMaterialDescription] = useState('');
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [professionMaterials, setProfessionMaterials] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadConfig, setUploadConfig] = useState(null);
  // Institutions management
  const [institutions, setInstitutions] = useState([]);
  const [showAddInstitution, setShowAddInstitution] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [editingFullInstitution, setEditingFullInstitution] = useState(null);
  const [savingInstitution, setSavingInstitution] = useState(false);
  const [newInstitution, setNewInstitution] = useState({
    name: '',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    tax_id: '',
    max_students: 100,
    price_per_student: 15,
    monthly_fee: 0,
    professions: []
  });
  // Audio management
  const [audios, setAudios] = useState([]);
  const [audioJobs, setAudioJobs] = useState([]);
  const [newAudio, setNewAudio] = useState({
    topic_id: '',
    profession_id: '',
    num_audios: 1,
    custom_text: '',
    audio_type: 'summary',
    title: '',
    max_duration: 180  // Max duration in seconds (3 min default)
  });
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioTopics, setAudioTopics] = useState([]);  // Topics for selected profession
  // Library management
  const [libraryResources, setLibraryResources] = useState([]);
  const [libraryStats, setLibraryStats] = useState(null);
  const [publishingResource, setPublishingResource] = useState(null);
  // Student management
  const [showCreateStudentModal, setShowCreateStudentModal] = useState(false);
  const [newStudent, setNewStudent] = useState({ email: '', name: '', password: '', profession_id: '', subscription_tier: 'trial' });
  const [creatingStudent, setCreatingStudent] = useState(false);
  // Scraper management
  const [scraperUrl, setScraperUrl] = useState('');
  const [scraperType, setScraperType] = useState('convocatorias');
  const [scraperSelector, setScraperSelector] = useState('');
  const [scraperProfession, setScraperProfession] = useState('');
  const [scraperPreview, setScraperPreview] = useState(null);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [scraperJobId, setScraperJobId] = useState(null);
  const [pendingContent, setPendingContent] = useState([]);
  const [pendingCounts, setPendingCounts] = useState({});
  const [scrapingLogs, setScrapingLogs] = useState([]);
  const [scrapingSources, setScrapingSources] = useState([]);

  useEffect(() => {
    loadData();
    loadUploadConfig();
  }, []);

  useEffect(() => {
    filterStudents();
  }, [students, searchTerm, filterProfession, filterRisk]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, studentsRes, professionsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/students/metrics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/professions`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data.students);
      setProfessions(professionsRes.data.professions || []);
    } catch (error) {
      toast.error('Error al cargar datos de administración');
    } finally {
      setLoading(false);
    }
  };

  const loadUploadConfig = async () => {
    try {
      const response = await axios.get(`${API}/files/upload-config`);
      setUploadConfig(response.data);
    } catch (error) {
      console.log('Error loading upload config');
    }
  };

  const filterStudents = () => {
    let filtered = [...students];
    
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterProfession !== 'all') {
      filtered = filtered.filter(s => s.profession === filterProfession);
    }
    
    if (filterRisk !== 'all') {
      filtered = filtered.filter(s => 
        s.abandonment_risk === filterRisk || s.failure_risk === filterRisk
      );
    }
    
    setFilteredStudents(filtered);
  };

  const loadStudentDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${API}/admin/students/${userId}/detailed`,
        { headers: getAuthHeaders() }
      );
      setStudentDetails(response.data);
      setSelectedStudent(userId);
    } catch (error) {
      toast.error('Error al cargar detalles del estudiante');
    }
  };

  const handleFileUpload = async () => {
    if (!file && !uploadConfig?.driveUrl) return;
    if (!uploadConfig?.profession_id) {
      toast.error('Debes seleccionar una profesión/OPE');
      return;
    }
    
    setUploading(true);
    
    try {
      // Build URL with query params for profession and region
      let url = `${API}/admin/questions/bulk?profession_id=${uploadConfig.profession_id}`;
      if (uploadConfig.region) {
        url += `&region=${uploadConfig.region}`;
      }
      
      let response;
      
      // Handle file upload directly
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        response = await axios.post(url, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      } 
      // Handle Google Drive URL
      else if (uploadConfig?.driveUrl) {
        const driveResponse = await axios.post(`${API}/drive/import`, {
          url: uploadConfig.driveUrl,
          profession_id: uploadConfig.profession_id,
          region: uploadConfig.region
        }, {
          headers: getAuthHeaders()
        });
        response = { data: driveResponse.data };
      }
      
      const result = response.data;
      toast.success(`${result.message || 'Carga completada'} para ${result.profession || uploadConfig.profession_id}${result.region ? ` (${result.region})` : ''}`);
      
      if (result.total_errors > 0) {
        toast.warn(`${result.total_errors} preguntas tuvieron errores`);
      }
      
      setFile(null);
      setUploadConfig({...uploadConfig, driveUrl: ''});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar preguntas');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateProfession = async () => {
    if (!newProfession.name) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await axios.post(`${API}/admin/professions`, newProfession, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('Profesión creada correctamente');
      setShowAddProfession(false);
      setNewProfession({ name: '', description: '', category: '', tutor_system_prompt: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear profesión');
    }
  };

  const handleDeleteProfession = async (professionId) => {
    if (!window.confirm('¿Estás seguro de desactivar esta profesión?')) return;
    try {
      await axios.delete(`${API}/admin/professions/${professionId}`, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('Profesión desactivada');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar profesión');
    }
  };

  const openExamConfigEditor = async (profession) => {
    try {
      const response = await axios.get(
        `${API}/admin/professions/${profession.profession_id}/exam-config`,
        { headers: getAuthHeaders() }
      );
      const config = response.data.exam_config;
      setExamConfig({
        total_questions: config.total_questions || 60,
        reserve_questions: config.reserve_questions || 12,
        time_limit_minutes: config.time_limit_minutes || 150,
        correct_points: config.scoring?.correct || 1.0,
        incorrect_points: config.scoring?.incorrect || -0.25,
        blank_points: config.scoring?.blank || 0,
        passing_score: config.passing_score || 50,
        wrong_answers_to_deduct: config.wrong_answers_to_deduct || 4
      });
      setEditingExamConfig(profession);
    } catch (error) {
      // If no config exists, use defaults
      setExamConfig({
        total_questions: 60,
        reserve_questions: 12,
        time_limit_minutes: 150,
        correct_points: 1.0,
        incorrect_points: -0.25,
        blank_points: 0,
        passing_score: 50,
        wrong_answers_to_deduct: 4
      });
      setEditingExamConfig(profession);
    }
  };

  const saveExamConfig = async () => {
    if (!editingExamConfig) return;
    try {
      await axios.put(
        `${API}/admin/professions/${editingExamConfig.profession_id}/exam-config`,
        examConfig,
        { headers: getAuthHeaders() }
      );
      toast.success('Configuración de examen guardada');
      setEditingExamConfig(null);
      loadData();
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  // Legal content functions
  const openLegalEditor = async (pageType) => {
    try {
      const response = await axios.get(`${API}/legal/${pageType}`);
      setLegalContent(response.data.content || '');
      setEditingLegalContent(pageType);
    } catch (error) {
      setLegalContent('');
      setEditingLegalContent(pageType);
    }
  };

  const saveLegalContent = async () => {
    if (!editingLegalContent) return;
    setSavingLegal(true);
    try {
      await axios.put(
        `${API}/legal/${editingLegalContent}`,
        { content: legalContent },
        { headers: getAuthHeaders() }
      );
      toast.success('Contenido legal guardado');
      setEditingLegalContent(null);
    } catch (error) {
      toast.error('Error al guardar contenido');
    } finally {
      setSavingLegal(false);
    }
  };

  // Load leads
  const loadLeads = async () => {
    try {
      const [contactRes, institutionalRes] = await Promise.all([
        axios.get(`${API}/admin/leads/contact`, { headers: getAuthHeaders() }).catch(() => ({ data: { leads: [] } })),
        axios.get(`${API}/admin/leads/institutional`, { headers: getAuthHeaders() }).catch(() => ({ data: { leads: [] } }))
      ]);
      setLeads(contactRes.data.leads || []);
      setInstitutionalLeads(institutionalRes.data.leads || []);
    } catch (error) {
      console.log('Error loading leads');
    }
  };

  // Institutions management functions
  const loadInstitutions = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/institutional/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInstitutions(response.data.institutions || []);
    } catch (error) {
      console.log('Error loading institutions');
    }
  };

  const handleCreateInstitution = async () => {
    if (!newInstitution.name || !newInstitution.contact_email || !newInstitution.contact_name) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/institutional/create`, newInstitution, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Institución creada correctamente');
      setShowAddInstitution(false);
      setNewInstitution({
        name: '',
        contact_email: '',
        contact_name: '',
        contact_phone: '',
        tax_id: '',
        max_students: 100,
        price_per_student: 15,
        monthly_fee: 0,
        professions: []
      });
      loadInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear institución');
    }
  };

  const handleDeleteInstitution = async (institutionId, institutionName) => {
    if (!window.confirm(`¿Estás seguro de eliminar la institución "${institutionName}"? Esta acción desvinculará a todos los estudiantes asociados.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/institutional/${institutionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Institución eliminada correctamente');
      loadInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar institución');
    }
  };

  const handleUpdateInstitutionPricing = async (institutionId, pricing) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/institutional/${institutionId}/pricing`, pricing, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Precios actualizados');
      loadInstitutions();
      setEditingInstitution(null);
    } catch (error) {
      toast.error('Error al actualizar precios');
    }
  };

  const handleUpdateFullInstitution = async () => {
    if (!editingFullInstitution?.name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSavingInstitution(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/institutional/${editingFullInstitution.institution_id}`, {
        name: editingFullInstitution.name,
        contact_email: editingFullInstitution.contact_email,
        contact_name: editingFullInstitution.contact_name,
        contact_phone: editingFullInstitution.contact_phone,
        tax_id: editingFullInstitution.tax_id,
        billing_email: editingFullInstitution.billing_email,
        address: editingFullInstitution.address,
        status: editingFullInstitution.status
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Institución actualizada correctamente');
      loadInstitutions();
      setEditingFullInstitution(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar institución');
    } finally {
      setSavingInstitution(false);
    }
  };

  // Audio management functions
  const loadAudios = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/audios/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAudios(response.data.audios || []);
    } catch (error) {
      console.log('Error loading audios');
    }
  };

  const generateAudio = async () => {
    if (!newAudio.profession_id) {
      toast.error('Selecciona una profesión');
      return;
    }
    
    setGeneratingAudio(true);
    try {
      const token = localStorage.getItem('session_token');
      
      if (newAudio.custom_text) {
        // Create custom audio from text
        await axios.post(`${API}/audios/custom`, {
          text: newAudio.custom_text,
          topic_id: newAudio.topic_id || 'general',
          profession_id: newAudio.profession_id,
          title: newAudio.title || 'Audio personalizado',
          section: 'resumenes'
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Audio creado correctamente');
      } else {
        // Generate from topic
        const response = await axios.post(`${API}/audios/generate`, newAudio, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(`Generación iniciada: ${response.data.job_id}`);
      }
      
      setNewAudio({
        topic_id: '',
        profession_id: '',
        num_audios: 1,
        custom_text: '',
        audio_type: 'summary',
        title: ''
      });
      loadAudios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const publishAudio = async (audioId, plans, section = 'temas') => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/audios/publish`, {
        audio_id: audioId,
        available_plans: plans,
        section: section
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Audio publicado');
      loadAudios();
    } catch (error) {
      toast.error('Error al publicar audio');
    }
  };

  const deleteAudio = async (audioId) => {
    if (!window.confirm('¿Eliminar este audio?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/audios/${audioId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Audio eliminado');
      loadAudios();
    } catch (error) {
      toast.error('Error al eliminar audio');
    }
  };

  // Library management functions
  const loadLibrary = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [resourcesRes, statsRes] = await Promise.all([
        axios.get(`${API}/library/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${API}/library/stats`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setLibraryResources([
        ...(resourcesRes.data.books || []),
        ...(resourcesRes.data.audios || []),
        ...(resourcesRes.data.materials || [])
      ]);
      setLibraryStats(statsRes.data);
    } catch (error) {
      console.log('Error loading library');
    }
  };

  const publishToLibrary = async (resourceId, resourceType, title, plans) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/library/publish`, {
        resource_id: resourceId,
        resource_type: resourceType,
        title: title,
        available_plans: plans
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Recurso publicado en la biblioteca');
      loadLibrary();
    } catch (error) {
      toast.error('Error al publicar recurso');
    }
  };

  // Scraper functions
  const previewScraping = async () => {
    if (!scraperUrl) {
      toast.error('Introduce una URL');
      return;
    }
    
    setScraperLoading(true);
    setScraperPreview(null);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/scraper/preview`, {
        url: scraperUrl,
        scraping_type: scraperType,
        css_selector: scraperSelector || null,
        profession_id: scraperProfession || null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setScraperPreview(response.data);
      setScraperJobId(response.data.job_id);
      toast.success(`Encontrados ${response.data.items_found} elementos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en scraping');
    } finally {
      setScraperLoading(false);
    }
  };

  const importScrapedContent = async () => {
    if (!scraperJobId || !scraperProfession) {
      toast.error('Selecciona una profesión destino');
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/scraper/import/${scraperJobId}?profession_id=${scraperProfession}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Contenido importado a cola de revisión');
      loadPendingContent();
      setScraperPreview(null);
      setScraperJobId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    }
  };

  const loadPendingContent = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/scraper/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingContent(response.data.items || []);
      setPendingCounts(response.data.counts || {});
    } catch (error) {
      console.log('Error loading pending content');
    }
  };

  const reviewContent = async (pendingId, action, editedContent = null) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/scraper/pending/${pendingId}/review`, {
        content_id: pendingId,
        action: action,
        edited_content: editedContent
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(action === 'approve' ? 'Contenido aprobado' : 'Contenido rechazado');
      loadPendingContent();
    } catch (error) {
      toast.error('Error en la revisión');
    }
  };

  const generateQuestionsFromTopic = async (pendingId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/scraper/generate-questions/${pendingId}?num_questions=10`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadPendingContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error generando preguntas');
    }
  };

  const generateFlashcardsFromTopic = async (pendingId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/scraper/generate-flashcards/${pendingId}?num_cards=10`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error generando flashcards');
    }
  };

  const loadScrapingLogs = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/scraper/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setScrapingLogs(response.data.logs || []);
    } catch (error) {
      console.log('Error loading logs');
    }
  };

  // API Keys functions
  const loadApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/api-keys`, {
        headers: { ...getAuthHeaders() }
      });
      setApiKeys(response.data.keys || {});
    } catch (error) {
      console.log('Error loading API keys');
    }
  };

  const saveApiKeys = async () => {
    setSavingKeys(true);
    try {
      await axios.put(`${API}/admin/settings/api-keys`, apiKeys, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('API Keys guardadas correctamente');
    } catch (error) {
      toast.error('Error al guardar API Keys');
    } finally {
      setSavingKeys(false);
    }
  };

  const toggleShowKey = (keyName) => {
    setShowKeys(prev => ({ ...prev, [keyName]: !prev[keyName] }));
  };

  // Material/Temario functions
  const loadProfessionMaterials = async (professionId) => {
    if (!professionId) return;
    try {
      const response = await axios.get(`${API}/files/profession/${professionId}/materials`, {
        headers: { ...getAuthHeaders() }
      });
      setProfessionMaterials(response.data.materials || []);
    } catch (error) {
      setProfessionMaterials([]);
    }
  };

  const uploadMaterial = async () => {
    if (!selectedProfessionForMaterial || !materialFile) {
      toast.error('Selecciona una profesión y un archivo');
      return;
    }

    setUploadingMaterial(true);
    setUploadProgress(0);
    setUploadStatus('Preparando...');

    const fileSizeMB = materialFile.size / (1024 * 1024);
    const simpleLimit = uploadConfig?.simple_upload_limit_mb || 100;

    try {
      // Use chunked upload for large files (>100MB)
      if (fileSizeMB > simpleLimit) {
        await uploadMaterialChunked();
      } else {
        // Simple upload for smaller files
        const formData = new FormData();
        formData.append('file', materialFile);
        formData.append('description', materialDescription);

        setUploadStatus('Subiendo archivo...');
        await axios.post(
          `${API}/files/simple/${selectedProfessionForMaterial}`,
          formData,
          { 
            headers: { 
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          }
        );
        
        toast.success('Material subido correctamente');
      }
      
      setMaterialFile(null);
      setMaterialDescription('');
      setUploadProgress(0);
      setUploadStatus('');
      loadProfessionMaterials(selectedProfessionForMaterial);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const uploadMaterialChunked = async () => {
    const chunkSize = uploadConfig?.chunk_size || 5 * 1024 * 1024; // 5MB default
    const totalChunks = Math.ceil(materialFile.size / chunkSize);
    
    setUploadStatus('Iniciando subida en fragmentos...');
    
    // Initialize chunked upload
    const initRes = await axios.post(`${API}/files/init-chunked`, {
      filename: materialFile.name,
      total_size: materialFile.size,
      total_chunks: totalChunks,
      profession_id: selectedProfessionForMaterial,
      description: materialDescription
    }, { headers: getAuthHeaders() });
    
    const { upload_id } = initRes.data;
    
    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, materialFile.size);
      const chunk = materialFile.slice(start, end);
      
      setUploadStatus(`Subiendo fragmento ${i + 1} de ${totalChunks}...`);
      
      await axios.post(
        `${API}/files/chunk/${upload_id}/${i}`,
        chunk,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'application/octet-stream'
          }
        }
      );
      
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
    
    // Complete upload
    setUploadStatus('Finalizando y procesando...');
    const completeRes = await axios.post(
      `${API}/files/complete/${upload_id}`,
      {},
      { headers: getAuthHeaders() }
    );
    
    if (completeRes.data.type === 'zip') {
      toast.success(`ZIP extraído: ${completeRes.data.extracted_files} archivos procesados`);
    } else {
      toast.success('Material subido correctamente');
    }
  };

  const deleteMaterial = async (materialId) => {
    if (!window.confirm('¿Eliminar este material?')) return;
    try {
      console.log('Deleting material:', materialId);
      const response = await axios.delete(`${API}/files/material/${materialId}`, {
        headers: { ...getAuthHeaders() }
      });
      console.log('Delete response:', response.data);
      toast.success('Material eliminado');
      loadProfessionMaterials(selectedProfessionForMaterial);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar material');
    }
  };

  // Student management functions
  const deleteStudent = async (userId, studentName) => {
    if (!window.confirm(`¿Eliminar al estudiante "${studentName}" y todos sus datos? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/students/${userId}`, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('Estudiante eliminado correctamente');
      loadData(); // Reload students list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar estudiante');
    }
  };

  const createStudent = async () => {
    if (!newStudent.email || !newStudent.name || !newStudent.password) {
      toast.error('Email, nombre y contraseña son obligatorios');
      return;
    }
    setCreatingStudent(true);
    try {
      await axios.post(`${API}/admin/students`, newStudent, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('Estudiante creado correctamente');
      setShowCreateStudentModal(false);
      setNewStudent({ email: '', name: '', password: '', profession_id: '', subscription_tier: 'trial' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear estudiante');
    } finally {
      setCreatingStudent(false);
    }
  };

  // Profession management functions
  const deleteProfession = async (professionId, professionName) => {
    if (!window.confirm(`¿Eliminar la profesión "${professionName}"? Los estudiantes que la tengan seleccionada no serán afectados.`)) return;
    try {
      await axios.delete(`${API}/admin/professions/${professionId}`, {
        headers: { ...getAuthHeaders() }
      });
      toast.success('Profesión eliminada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar profesión');
    }
  };

  const getRiskBadge = (risk) => {
    const colors = {
      alto: 'bg-rose-100 text-rose-700 border-rose-200',
      medio: 'bg-amber-100 text-amber-700 border-amber-200',
      bajo: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    return colors[risk] || colors.bajo;
  };

  const getProbabilityBadge = (prob) => {
    const colors = {
      alta: 'bg-emerald-100 text-emerald-700',
      media: 'bg-amber-100 text-amber-700',
      baja: 'bg-rose-100 text-rose-700'
    };
    return colors[prob] || colors.baja;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <h1 className="text-2xl font-extrabold font-heading text-indigo-600">
              {t('admin.title', 'Panel de Administración')} - TuPlazaIA
            </h1>
            <div className="flex items-center gap-3">
              <LanguageSelector variant="outline" showFlag={true} showName={true} />
              <Button 
                variant="outline" 
                onClick={() => navigate('/institutional')}
                className="border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Dashboard Institucional
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {t('common.back', 'Volver al Dashboard')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'students', label: 'Estudiantes', icon: Users },
              { id: 'professions', label: 'Profesiones', icon: Briefcase },
              { id: 'countries', label: 'Países', icon: Globe },
              { id: 'institutions', label: 'Instituciones', icon: Building2 },
              { id: 'credits', label: 'Créditos', icon: CreditCard },
              { id: 'plans', label: 'Planes', icon: Package },
              { id: 'pricing', label: 'Precios B2C', icon: Tag },
              { id: 'costs', label: 'Calc. Costes', icon: DollarSign },
              { id: 'drive', label: 'Google Drive', icon: Cloud },
              { id: 'content', label: 'Generar Contenido', icon: Sparkles },
              { id: 'content-mgmt', label: 'Gestionar Contenido', icon: FileSearch },
              { id: 'audit', label: 'Auditoría', icon: AlertTriangle },
              { id: 'live-classes', label: 'Clases en Vivo', icon: Video },
              { id: 'scraper', label: 'Scraper', icon: Database },
              { id: 'library', label: 'Biblioteca', icon: BookOpen },
              { id: 'audios', label: 'Audios IA', icon: Volume2 },
              { id: 'materials', label: 'Temarios', icon: FolderOpen },
              { id: 'books', label: 'Libros KDP', icon: BookMarked },
              { id: 'upload', label: 'Cargar Preguntas', icon: Upload },
              { id: 'legal', label: 'Legal', icon: FileText },
              { id: 'leads', label: 'Leads', icon: UserCheck },
              { id: 'campaigns', label: 'Campañas', icon: Mail },
              { id: 'invoices', label: 'Facturas', icon: FileText },
              { id: 'system', label: 'Sistema', icon: Power },
              { id: 'ai', label: 'IA Config', icon: Bot },
              { id: 'settings', label: 'API Keys', icon: Key }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <span className="text-sm text-slate-600">Total Usuarios</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-600">Exámenes Realizados</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.total_exams}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm text-slate-600">Preguntas en BD</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">{stats.total_questions}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm text-slate-600">En Riesgo Abandono</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {students.filter(s => s.abandonment_risk === 'alto').length}
                </p>
              </div>
            </div>

            {/* Users by Tier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold mb-4">Usuarios por Plan</h3>
                <div className="space-y-3">
                  {Object.entries(stats.users_by_tier || {}).map(([tier, count]) => (
                    <div key={tier} className="flex items-center justify-between">
                      <span className="text-slate-600 capitalize">{tier}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-600 rounded-full"
                            style={{ width: `${(count / stats.total_users) * 100}%` }}
                          />
                        </div>
                        <span className="font-bold text-slate-900 w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold mb-4">Usuarios por Profesión</h3>
                <div className="space-y-3">
                  {Object.entries(stats.users_by_profession || {}).map(([prof, count]) => (
                    <div key={prof} className="flex items-center justify-between">
                      <span className="text-slate-600 text-sm truncate max-w-[200px]">{prof}</span>
                      <span className="font-bold text-slate-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Risk Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold mb-4">Resumen de Riesgos</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-slate-600 mb-2">Riesgo de Abandono</p>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-sm">
                      Alto: {students.filter(s => s.abandonment_risk === 'alto').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm">
                      Medio: {students.filter(s => s.abandonment_risk === 'medio').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm">
                      Bajo: {students.filter(s => s.abandonment_risk === 'bajo').length}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-2">Riesgo de Suspender</p>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-sm">
                      Alto: {students.filter(s => s.failure_risk === 'alto').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm">
                      Medio: {students.filter(s => s.failure_risk === 'medio').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm">
                      Bajo: {students.filter(s => s.failure_risk === 'bajo').length}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-2">Probabilidad de Aprobar</p>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm">
                      Alta: {students.filter(s => s.pass_probability === 'alta').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm">
                      Media: {students.filter(s => s.pass_probability === 'media').length}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-sm">
                      Baja: {students.filter(s => s.pass_probability === 'baja').length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por email o nombre..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 rounded-lg"
                    />
                  </div>
                </div>
                <select
                  value={filterProfession}
                  onChange={(e) => setFilterProfession(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                >
                  <option value="all">Todas las profesiones</option>
                  {[...new Set(students.map(s => s.profession))].map(prof => (
                    <option key={prof} value={prof}>{prof}</option>
                  ))}
                </select>
                <select
                  value={filterRisk}
                  onChange={(e) => setFilterRisk(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                >
                  <option value="all">Todos los niveles de riesgo</option>
                  <option value="alto">Riesgo Alto</option>
                  <option value="medio">Riesgo Medio</option>
                  <option value="bajo">Riesgo Bajo</option>
                </select>
                <Button 
                  onClick={() => setShowCreateStudentModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Añadir Estudiante
                </Button>
              </div>
            </div>

            {/* Create Student Modal */}
            {showCreateStudentModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-bold mb-4">Crear Nuevo Estudiante</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <Input
                        type="email"
                        value={newStudent.email}
                        onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                        placeholder="estudiante@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                      <Input
                        value={newStudent.name}
                        onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                        placeholder="Nombre completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña *</label>
                      <Input
                        type="password"
                        value={newStudent.password}
                        onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Profesión</label>
                      <select
                        value={newStudent.profession_id}
                        onChange={(e) => setNewStudent({...newStudent, profession_id: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      >
                        <option value="">Sin asignar</option>
                        {professions.map(p => (
                          <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Plan</label>
                      <select
                        value={newStudent.subscription_tier}
                        onChange={(e) => setNewStudent({...newStudent, subscription_tier: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      >
                        <option value="trial">Trial</option>
                        <option value="basic">Básico</option>
                        <option value="premium">Premium</option>
                        <option value="top">Top</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCreateStudentModal(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={createStudent}
                      disabled={creatingStudent}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {creatingStudent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Crear
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Students Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Estudiante</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Profesión</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Plan</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Exámenes</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Horas</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Nota Media</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Riesgo Abandono</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Riesgo Suspender</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Prob. Aprobar</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredStudents.map((student) => (
                      <tr key={student.user_id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{student.name || 'Sin nombre'}</p>
                            <p className="text-sm text-slate-500">{student.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 max-w-[150px] truncate">
                          {student.profession}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            student.subscription_tier === 'premium' ? 'bg-purple-100 text-purple-700' :
                            student.subscription_tier === 'top' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {student.subscription_tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center font-medium">{student.total_exams}</td>
                        <td className="px-4 py-3 text-center font-medium">{student.practice_hours}h</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${
                            student.average_score >= 70 ? 'text-emerald-600' :
                            student.average_score >= 50 ? 'text-amber-600' :
                            'text-rose-600'
                          }`}>
                            {student.average_score}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadge(student.abandonment_risk)}`}>
                            {student.abandonment_risk}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadge(student.failure_risk)}`}>
                            {student.failure_risk}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProbabilityBadge(student.pass_probability)}`}>
                            {student.pass_probability}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => loadStudentDetails(student.user_id)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => deleteStudent(student.user_id, student.name || student.email)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredStudents.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No se encontraron estudiantes con los filtros seleccionados
                </div>
              )}
            </div>

            {/* Student Details Modal */}
            {selectedStudent && studentDetails && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-slate-200 flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold">{studentDetails.user?.name || 'Estudiante'}</h2>
                      <p className="text-slate-600">{studentDetails.user?.email}</p>
                    </div>
                    <Button variant="ghost" onClick={() => setSelectedStudent(null)}>
                      <XCircle className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* User Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">Plan</p>
                        <p className="font-bold capitalize">{studentDetails.user?.subscription_tier}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">Créditos</p>
                        <p className="font-bold">{studentDetails.user?.credits}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">Fecha Registro</p>
                        <p className="font-bold text-sm">
                          {new Date(studentDetails.user?.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">Temas Practicados</p>
                        <p className="font-bold">{studentDetails.total_topics_practiced}</p>
                      </div>
                    </div>

                    {/* Topic Performance */}
                    <div>
                      <h3 className="font-bold mb-3">Rendimiento por Tema (de peor a mejor)</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {studentDetails.topic_performance?.map((topic, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{topic.name}</p>
                              <div className="flex gap-2 text-xs text-slate-500">
                                <span className="text-emerald-600">{topic.correct} correctas</span>
                                <span className="text-rose-600">{topic.incorrect} incorrectas</span>
                                <span>{topic.blank} en blanco</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`font-bold ${
                                topic.percentage >= 70 ? 'text-emerald-600' :
                                topic.percentage >= 50 ? 'text-amber-600' :
                                'text-rose-600'
                              }`}>
                                {topic.percentage}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Exams */}
                    <div>
                      <h3 className="font-bold mb-3">Últimos Exámenes</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {studentDetails.exams?.slice(0, 10).map((exam, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium capitalize">{exam.exam_type}</p>
                              <p className="text-xs text-slate-500">
                                {new Date(exam.started_at).toLocaleDateString('es-ES')}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`font-bold ${
                                exam.score >= 70 ? 'text-emerald-600' :
                                exam.score >= 50 ? 'text-amber-600' :
                                'text-rose-600'
                              }`}>
                                {exam.score?.toFixed(1) || 0}%
                              </span>
                              <p className="text-xs text-slate-500">{exam.total_questions} preguntas</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Carga Masiva de Preguntas</h2>
                  <p className="text-slate-600 text-sm">Sube preguntas en formato CSV, Excel o ZIP</p>
                </div>
              </div>

              {/* Profession and Region Selection */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-2">
                    Profesión/OPE *
                  </label>
                  <select
                    value={uploadConfig?.profession_id || ''}
                    onChange={(e) => setUploadConfig({...uploadConfig, profession_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">Seleccionar profesión...</option>
                    {professions.map(p => (
                      <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-2">
                    Comunidad Autónoma (opcional)
                  </label>
                  <select
                    value={uploadConfig?.region || ''}
                    onChange={(e) => setUploadConfig({...uploadConfig, region: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">Todas las regiones</option>
                    <option value="andalucia">Andalucía</option>
                    <option value="aragon">Aragón</option>
                    <option value="asturias">Asturias</option>
                    <option value="baleares">Islas Baleares</option>
                    <option value="canarias">Canarias</option>
                    <option value="cantabria">Cantabria</option>
                    <option value="castilla_leon">Castilla y León</option>
                    <option value="castilla_la_mancha">Castilla-La Mancha</option>
                    <option value="cataluna">Cataluña</option>
                    <option value="comunidad_valenciana">Comunidad Valenciana</option>
                    <option value="extremadura">Extremadura</option>
                    <option value="galicia">Galicia</option>
                    <option value="madrid">Madrid</option>
                    <option value="murcia">Murcia</option>
                    <option value="navarra">Navarra</option>
                    <option value="pais_vasco">País Vasco</option>
                    <option value="la_rioja">La Rioja</option>
                    <option value="nacional">Nacional (Estado)</option>
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Formato requerido:</h3>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>topic_id - ID del tema (opcional si se selecciona profesión)</li>
                      <li>question_text - Texto de la pregunta</li>
                      <li>difficulty - fácil, media, difícil</li>
                      <li>option_a, option_b, option_c, option_d</li>
                      <li>correct_option - A, B, C o D</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Google Drive Import */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <h4 className="font-medium text-emerald-900 mb-2">📂 Importar desde Google Drive</h4>
                <div className="space-y-2">
                  <Input
                    value={uploadConfig?.driveUrl || ''}
                    onChange={(e) => setUploadConfig({...uploadConfig, driveUrl: e.target.value})}
                    placeholder="Pega el enlace de Google Drive..."
                    className="rounded-lg"
                  />
                  <p className="text-xs text-emerald-600">
                    Soporta enlaces públicos y privados (requiere autenticación con Google)
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.zip"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="rounded-xl"
                />
                {file && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>{file.name}</span>
                  </div>
                )}
                <Button
                  onClick={handleFileUpload}
                  disabled={(!file && !uploadConfig?.driveUrl) || uploading || !uploadConfig?.profession_id}
                  className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 font-bold"
                >
                  {uploading ? 'Cargando...' : 'Cargar Preguntas'}
                </Button>
                {!uploadConfig?.profession_id && (
                  <p className="text-center text-sm text-amber-600">
                    ⚠️ Selecciona una profesión antes de cargar preguntas
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Countries Tab */}
        {activeTab === 'countries' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Gestión de Países</h2>
              <p className="text-slate-600">Configura países, regiones y disponibilidad</p>
            </div>
            <CountriesManagement token={localStorage.getItem('session_token')} />
          </div>
        )}

        {/* Institutions Tab */}
        {activeTab === 'institutions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Gestión de Instituciones</h2>
                <p className="text-slate-600 text-sm">Crea y gestiona instituciones con precios personalizados</p>
              </div>
              <Button onClick={() => { setShowAddInstitution(true); loadInstitutions(); }} className="rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Institución
              </Button>
            </div>

            {/* Load institutions on tab mount */}
            {institutions.length === 0 && (
              <div className="text-center py-8">
                <Button variant="outline" onClick={loadInstitutions}>
                  Cargar Instituciones
                </Button>
              </div>
            )}

            {/* Institutions List */}
            <div className="grid gap-4">
              {institutions.map((institution) => (
                <div key={institution.institution_id} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{institution.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            institution.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {institution.status === 'active' ? 'Activa' : 'Inactiva'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Contacto</p>
                          <p className="font-medium text-slate-900">{institution.contact_name}</p>
                          <p className="text-sm text-slate-600">{institution.contact_email}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Estudiantes</p>
                          <p className="font-medium text-slate-900">
                            {institution.current_students || 0} / {institution.max_students}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Precio/estudiante</p>
                          <p className="font-medium text-emerald-600">
                            {institution.price_per_student || 15}€/mes
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Cuota fija mensual</p>
                          <p className="font-medium text-indigo-600">
                            {institution.monthly_fee || 0}€
                          </p>
                        </div>
                      </div>

                      {institution.tax_id && (
                        <p className="text-sm text-slate-500 mt-2">CIF/NIF: {institution.tax_id}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditingInstitution(institution)}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Precios
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingFullInstitution({...institution})}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteInstitution(institution.institution_id, institution.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Institution Modal */}
            {showAddInstitution && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <h2 className="text-xl font-bold mb-6">Nueva Institución</h2>
                  
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Nombre de la institución *</label>
                        <Input
                          value={newInstitution.name}
                          onChange={(e) => setNewInstitution({...newInstitution, name: e.target.value})}
                          placeholder="Academia XYZ"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">CIF/NIF</label>
                        <Input
                          value={newInstitution.tax_id}
                          onChange={(e) => setNewInstitution({...newInstitution, tax_id: e.target.value})}
                          placeholder="B12345678"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="font-medium text-slate-900 mb-3">Datos de contacto</h4>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Nombre del contacto *</label>
                          <Input
                            value={newInstitution.contact_name}
                            onChange={(e) => setNewInstitution({...newInstitution, contact_name: e.target.value})}
                            placeholder="Juan García"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Email del contacto *</label>
                          <Input
                            type="email"
                            value={newInstitution.contact_email}
                            onChange={(e) => setNewInstitution({...newInstitution, contact_email: e.target.value})}
                            placeholder="contacto@academia.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Teléfono</label>
                          <Input
                            value={newInstitution.contact_phone}
                            onChange={(e) => setNewInstitution({...newInstitution, contact_phone: e.target.value})}
                            placeholder="+34 600 000 000"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="font-medium text-slate-900 mb-3">Configuración y precios</h4>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Máx. estudiantes</label>
                          <Input
                            type="number"
                            value={newInstitution.max_students}
                            onChange={(e) => setNewInstitution({...newInstitution, max_students: parseInt(e.target.value) || 100})}
                            className="mt-1"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Precio/estudiante (€/mes)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newInstitution.price_per_student}
                            onChange={(e) => setNewInstitution({...newInstitution, price_per_student: parseFloat(e.target.value) || 0})}
                            className="mt-1"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Cuota fija mensual (€)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newInstitution.monthly_fee}
                            onChange={(e) => setNewInstitution({...newInstitution, monthly_fee: parseFloat(e.target.value) || 0})}
                            className="mt-1"
                            min="0"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Factura mensual estimada: {newInstitution.monthly_fee}€ + ({newInstitution.max_students} × {newInstitution.price_per_student}€) = 
                        <span className="font-bold text-slate-700"> {(newInstitution.monthly_fee + (newInstitution.max_students * newInstitution.price_per_student)).toFixed(2)}€/mes</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setShowAddInstitution(false)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button onClick={handleCreateInstitution} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
                      Crear Institución
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Pricing Modal */}
            {editingInstitution && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full p-6">
                  <h2 className="text-xl font-bold mb-2">Editar Precios</h2>
                  <p className="text-slate-600 mb-6">{editingInstitution.name}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Máximo estudiantes</label>
                      <Input
                        type="number"
                        value={editingInstitution.max_students}
                        onChange={(e) => setEditingInstitution({
                          ...editingInstitution, 
                          max_students: parseInt(e.target.value) || 100
                        })}
                        className="mt-1"
                        min="1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Actuales: {editingInstitution.current_students || 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Precio por estudiante (€/mes)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingInstitution.price_per_student || 15}
                        onChange={(e) => setEditingInstitution({
                          ...editingInstitution, 
                          price_per_student: parseFloat(e.target.value) || 0
                        })}
                        className="mt-1"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Cuota fija mensual (€)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingInstitution.monthly_fee || 0}
                        onChange={(e) => setEditingInstitution({
                          ...editingInstitution, 
                          monthly_fee: parseFloat(e.target.value) || 0
                        })}
                        className="mt-1"
                        min="0"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Cuota base independiente del número de estudiantes
                      </p>
                    </div>

                    <div className="bg-indigo-50 rounded-xl p-4 mt-4">
                      <p className="text-sm text-indigo-700 font-medium">Factura mensual estimada</p>
                      <p className="text-2xl font-bold text-indigo-600">
                        {((editingInstitution.monthly_fee || 0) + 
                          ((editingInstitution.current_students || 0) * (editingInstitution.price_per_student || 15))
                        ).toFixed(2)}€
                      </p>
                      <p className="text-xs text-indigo-500 mt-1">
                        {editingInstitution.monthly_fee || 0}€ (fija) + {editingInstitution.current_students || 0} estudiantes × {editingInstitution.price_per_student || 15}€
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setEditingInstitution(null)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={() => handleUpdateInstitutionPricing(editingInstitution.institution_id, {
                        max_students: editingInstitution.max_students,
                        price_per_student: editingInstitution.price_per_student,
                        monthly_fee: editingInstitution.monthly_fee
                      })} 
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Full Institution Modal */}
            {editingFullInstitution && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-bold">Editar Institución</h3>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                      <Input
                        value={editingFullInstitution.name || ''}
                        onChange={(e) => setEditingFullInstitution({...editingFullInstitution, name: e.target.value})}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Contacto</label>
                        <Input
                          value={editingFullInstitution.contact_name || ''}
                          onChange={(e) => setEditingFullInstitution({...editingFullInstitution, contact_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email Contacto</label>
                        <Input
                          type="email"
                          value={editingFullInstitution.contact_email || ''}
                          onChange={(e) => setEditingFullInstitution({...editingFullInstitution, contact_email: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                        <Input
                          value={editingFullInstitution.contact_phone || ''}
                          onChange={(e) => setEditingFullInstitution({...editingFullInstitution, contact_phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">CIF/NIF</label>
                        <Input
                          value={editingFullInstitution.tax_id || ''}
                          onChange={(e) => setEditingFullInstitution({...editingFullInstitution, tax_id: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Dirección</label>
                      <Input
                        value={editingFullInstitution.address || ''}
                        onChange={(e) => setEditingFullInstitution({...editingFullInstitution, address: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email Facturación</label>
                      <Input
                        type="email"
                        value={editingFullInstitution.billing_email || ''}
                        onChange={(e) => setEditingFullInstitution({...editingFullInstitution, billing_email: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                      <select
                        value={editingFullInstitution.status || 'active'}
                        onChange={(e) => setEditingFullInstitution({...editingFullInstitution, status: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      >
                        <option value="active">Activa</option>
                        <option value="inactive">Inactiva</option>
                        <option value="suspended">Suspendida</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="p-6 border-t border-slate-200 flex gap-3">
                    <Button variant="outline" onClick={() => setEditingFullInstitution(null)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleUpdateFullInstitution}
                      disabled={savingInstitution}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {savingInstitution ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Professions Tab - Using new ProfessionsManagement component */}
        {activeTab === 'professions' && (
          <ProfessionsManagement />
        )}

        {/* Legal Content Tab */}
        {activeTab === 'legal' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Contenido Legal</h2>
                <p className="text-slate-600">Gestiona el Aviso Legal y la Política de Privacidad</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold">Aviso Legal</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Información legal de la empresa, condiciones de uso y propiedad intelectual.
                </p>
                <Button onClick={() => openLegalEditor('aviso-legal')} className="w-full">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Aviso Legal
                </Button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold">Política de Privacidad</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  RGPD, tratamiento de datos personales y derechos de los usuarios.
                </p>
                <Button onClick={() => openLegalEditor('privacidad')} className="w-full">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Privacidad
                </Button>
              </div>
            </div>

            {/* Legal Editor Modal */}
            {editingLegalContent && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-slate-200">
                    <h2 className="text-xl font-bold">
                      Editar {editingLegalContent === 'aviso-legal' ? 'Aviso Legal' : 'Política de Privacidad'}
                    </h2>
                    <p className="text-sm text-slate-600">Usa HTML para dar formato al contenido</p>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-6">
                    <textarea
                      value={legalContent}
                      onChange={(e) => setLegalContent(e.target.value)}
                      className="w-full h-96 px-4 py-3 border border-slate-200 rounded-xl font-mono text-sm resize-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                      placeholder="<h2>Título</h2><p>Contenido...</p>"
                    />
                    
                    <div className="mt-4">
                      <h4 className="font-medium text-slate-700 mb-2">Vista previa:</h4>
                      <div 
                        className="prose prose-sm max-w-none p-4 bg-slate-50 rounded-lg border border-slate-200 max-h-48 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: legalContent }}
                      />
                    </div>
                  </div>
                  
                  <div className="p-6 border-t border-slate-200 flex gap-3">
                    <Button variant="outline" onClick={() => setEditingLegalContent(null)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={saveLegalContent} 
                      disabled={savingLegal}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {savingLegal ? 'Guardando...' : 'Guardar Contenido'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Sistema de Créditos</h2>
              <p className="text-slate-600">Configura costes, límites y paquetes de compra</p>
            </div>
            <CreditsManagement token={localStorage.getItem('session_token')} />
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Gestión de Planes</h2>
              <p className="text-slate-600">Crea y configura planes de suscripción</p>
            </div>
            <PlansManagement token={localStorage.getItem('session_token')} />
          </div>
        )}

        {/* Profession Pricing Tab */}
        {activeTab === 'pricing' && (
          <ProfessionPricing />
        )}

        {/* Cost Calculator Tab */}
        {activeTab === 'costs' && (
          <CostCalculator />
        )}

        {/* Google Drive Tab */}
        {activeTab === 'drive' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Google Drive</h2>
              <p className="text-slate-600">Importa material de estudio desde Google Drive</p>
            </div>
            <GoogleDriveManager token={localStorage.getItem('session_token')} professions={professions} />
          </div>
        )}

        {/* Content Generation Tab */}
        {activeTab === 'content' && (
          <ContentGenerationFlow professions={professions} />
        )}

        {/* Content Management Tab */}
        {activeTab === 'content-mgmt' && (
          <ContentManagement professions={professions} />
        )}

        {/* Content Audit Tab */}
        {activeTab === 'audit' && (
          <ContentAudit professions={professions} />
        )}

        {/* Live Classes Tab */}
        {activeTab === 'live-classes' && (
          <LiveClassesManagement professions={professions} institutions={institutions} />
        )}

        {/* Scraper Tab */}
        {activeTab === 'scraper' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Scraper de Oposiciones</h2>
                <p className="text-slate-600">Importa convocatorias, temarios y exámenes de fuentes oficiales</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={loadPendingContent}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </div>

            {/* Pending Items Alert */}
            {(pendingCounts.convocatorias > 0 || pendingCounts.temario > 0 || pendingCounts.examenes > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Contenido pendiente de revisión</p>
                    <p className="text-sm text-amber-700">
                      {pendingCounts.convocatorias || 0} convocatorias · {pendingCounts.temario || 0} temas · {pendingCounts.examenes || 0} preguntas
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Scraping Form */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Nuevo Scraping</h3>
                  <p className="text-sm text-slate-500">Extrae contenido de webs oficiales o PDFs</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Left column - Config */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Tipo de scraping</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'convocatorias', label: 'Convocatorias' },
                        { value: 'temario', label: 'Temario' },
                        { value: 'examenes', label: 'Exámenes' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setScraperType(opt.value)}
                          className={`px-4 py-2 rounded-lg font-medium transition-all ${
                            scraperType === opt.value
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">
                      URL de la fuente *
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={scraperUrl}
                        onChange={(e) => setScraperUrl(e.target.value)}
                        placeholder="https://boe.es/... o URL de PDF"
                        className="flex-1"
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => window.open(scraperUrl, '_blank')}
                        disabled={!scraperUrl}
                      >
                        <Link className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">
                      Selector CSS (opcional)
                    </label>
                    <Input
                      value={scraperSelector}
                      onChange={(e) => setScraperSelector(e.target.value)}
                      placeholder=".article, .resultado, tr.item..."
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Deja vacío para detección automática
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">
                      Vincular a profesión/OPE
                    </label>
                    <select
                      value={scraperProfession}
                      onChange={(e) => setScraperProfession(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="">Seleccionar profesión...</option>
                      {professions.map(p => (
                        <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <Button
                    onClick={previewScraping}
                    disabled={!scraperUrl || scraperLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6"
                  >
                    {scraperLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando...</>
                    ) : (
                      <><FileSearch className="w-4 h-4 mr-2" /> Previsualizar</>
                    )}
                  </Button>
                </div>

                {/* Right column - Preview */}
                <div className="bg-slate-50 rounded-xl p-4 min-h-[300px]">
                  <h4 className="font-medium text-slate-900 mb-3">Vista Previa</h4>
                  
                  {!scraperPreview ? (
                    <div className="flex items-center justify-center h-64 text-slate-400">
                      <div className="text-center">
                        <FileSearch className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Introduce una URL y pulsa Previsualizar</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-emerald-600 font-medium">
                          ✓ {scraperPreview.items_found} elementos encontrados
                        </span>
                        <span className="text-xs text-slate-500">
                          Job: {scraperPreview.job_id}
                        </span>
                      </div>
                      
                      {scraperPreview.preview?.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200 text-sm">
                          {scraperType === 'convocatorias' && (
                            <>
                              <p className="font-medium text-slate-900">{item.titulo}</p>
                              <p className="text-slate-500 text-xs mt-1">
                                {item.fecha} · {item.plazas || 'Plazas no especificadas'}
                              </p>
                            </>
                          )}
                          {scraperType === 'temario' && (
                            <>
                              <span className="text-indigo-600 font-medium">Tema {item.numero}:</span>{' '}
                              <span className="text-slate-700">{item.titulo}</span>
                            </>
                          )}
                          {scraperType === 'examenes' && (
                            <>
                              <p className="font-medium text-slate-900">{item.numero}. {item.pregunta?.substring(0, 100)}...</p>
                              <div className="mt-1 text-xs text-slate-500">
                                {Object.entries(item.opciones || {}).map(([k, v]) => (
                                  <span key={k} className="mr-2">{k}) {v?.substring(0, 30)}...</span>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {scraperPreview.total_results > scraperPreview.preview?.length && (
                        <p className="text-center text-sm text-slate-500 py-2">
                          ... y {scraperPreview.total_results - scraperPreview.preview?.length} más
                        </p>
                      )}

                      <Button
                        onClick={importScrapedContent}
                        disabled={!scraperProfession}
                        className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Importar a Cola de Revisión
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pending Content Review */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900">Contenido Pendiente de Revisión</h3>
                <Button variant="outline" size="sm" onClick={loadPendingContent}>
                  Actualizar
                </Button>
              </div>

              {pendingContent.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay contenido pendiente de revisión</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {pendingContent.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            item.content_type === 'convocatorias' ? 'bg-blue-100 text-blue-700' :
                            item.content_type === 'temario' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {item.content_type}
                          </span>
                          {item.source === 'ai_generated' && (
                            <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                              ✨ Generado por IA
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-slate-900">
                          {item.content?.titulo || item.content?.pregunta?.substring(0, 80) || 'Sin título'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Fuente: {item.source_url?.substring(0, 50) || item.source || 'N/A'}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-emerald-600 border-emerald-200"
                            onClick={() => reviewContent(item.pending_id, 'approve')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-rose-600 border-rose-200"
                            onClick={() => reviewContent(item.pending_id, 'reject')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                        {item.content_type === 'temario' && (
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-xs"
                              onClick={() => generateQuestionsFromTopic(item.pending_id)}
                            >
                              <Sparkles className="w-3 h-3 mr-1" />
                              Generar Preguntas
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fuentes oficiales de referencia */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-medium text-blue-900 mb-2">🇪🇸 Fuentes oficiales de España</h4>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-700">
                <a href="https://boe.es" target="_blank" rel="noopener noreferrer" className="hover:underline">• BOE (boe.es)</a>
                <a href="https://www.juntadeandalucia.es/boja" target="_blank" rel="noopener noreferrer" className="hover:underline">• BOJA (Andalucía)</a>
                <a href="https://dogc.gencat.cat" target="_blank" rel="noopener noreferrer" className="hover:underline">• DOGC (Cataluña)</a>
                <a href="https://www.bocm.es" target="_blank" rel="noopener noreferrer" className="hover:underline">• BOCM (Madrid)</a>
                <a href="https://dogv.gva.es" target="_blank" rel="noopener noreferrer" className="hover:underline">• DOGV (Valencia)</a>
                <a href="https://www.xunta.gal/dog" target="_blank" rel="noopener noreferrer" className="hover:underline">• DOG (Galicia)</a>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                💡 También soporta PDFs directos de temarios y exámenes anteriores
              </p>
            </div>
          </div>
        )}

        {/* Library Tab */}
        {activeTab === 'library' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Biblioteca de Recursos</h2>
                <p className="text-slate-600">Gestiona libros, audios y materiales disponibles para los usuarios</p>
              </div>
              <Button onClick={loadLibrary} variant="outline">
                Actualizar
              </Button>
            </div>

            {/* Stats */}
            {libraryStats && (
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-2xl font-bold text-indigo-600">{libraryStats.total_books || 0}</p>
                  <p className="text-sm text-slate-600">Libros</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-2xl font-bold text-purple-600">{libraryStats.total_audios || 0}</p>
                  <p className="text-sm text-slate-600">Audios</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-2xl font-bold text-emerald-600">{libraryStats.total_materials || 0}</p>
                  <p className="text-sm text-slate-600">Materiales</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-2xl font-bold text-slate-900">{libraryStats.total_resources || 0}</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
              </div>
            )}

            {/* Resources List */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Recursos Publicados</h3>
              {libraryResources.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay recursos publicados</p>
                  <p className="text-sm text-slate-400 mt-1">Publica libros desde la pestaña Libros KDP o audios desde Audios IA</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {libraryResources.map((resource, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          resource.resource_type === 'book' ? 'bg-indigo-100' :
                          resource.resource_type === 'audio' ? 'bg-purple-100' : 'bg-emerald-100'
                        }`}>
                          {resource.resource_type === 'book' && <BookMarked className="w-5 h-5 text-indigo-600" />}
                          {resource.resource_type === 'audio' && <Volume2 className="w-5 h-5 text-purple-600" />}
                          {resource.resource_type === 'material' && <FileText className="w-5 h-5 text-emerald-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{resource.title}</p>
                          <p className="text-sm text-slate-500">
                            Planes: {resource.available_plans?.join(', ') || 'Todos'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{resource.views || 0} vistas</span>
                        <Button variant="ghost" size="sm" onClick={() => setPublishingResource(resource)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Edit Resource Modal */}
            {publishingResource && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full p-6">
                  <h2 className="text-xl font-bold mb-4">Editar Acceso al Recurso</h2>
                  <p className="text-slate-600 mb-4">{publishingResource.title}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">Planes con acceso</label>
                      <div className="space-y-2">
                        {['top', 'premium', 'institutional'].map(plan => (
                          <label key={plan} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={publishingResource.available_plans?.includes(plan)}
                              onChange={(e) => {
                                const plans = publishingResource.available_plans || [];
                                if (e.target.checked) {
                                  setPublishingResource({...publishingResource, available_plans: [...plans, plan]});
                                } else {
                                  setPublishingResource({...publishingResource, available_plans: plans.filter(p => p !== plan)});
                                }
                              }}
                              className="rounded"
                            />
                            <span className="capitalize">{plan}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setPublishingResource(null)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={() => {
                        publishToLibrary(
                          publishingResource.resource_id,
                          publishingResource.resource_type,
                          publishingResource.title,
                          publishingResource.available_plans || []
                        );
                        setPublishingResource(null);
                      }}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audios Tab */}
        {activeTab === 'audios' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Audios con IA</h2>
                <p className="text-slate-600">Genera resúmenes de audio con ElevenLabs</p>
              </div>
              <Button onClick={loadAudios} variant="outline">
                Actualizar
              </Button>
            </div>

            {/* Generator */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Mic className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Generador de Audios</h3>
                  <p className="text-sm text-slate-500">Crea audios desde texto o temas existentes</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* From Topic */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-900">Desde Tema</h4>
                  <div>
                    <label className="text-sm text-slate-600 block mb-1">Profesión</label>
                    <select
                      value={newAudio.profession_id}
                      onChange={async (e) => {
                        const profId = e.target.value;
                        setNewAudio({...newAudio, profession_id: profId, topic_id: ''});
                        // Load topics for this profession
                        if (profId) {
                          try {
                            const token = localStorage.getItem('session_token');
                            const res = await axios.get(`${API}/by-profession?profession_id=${profId}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            // Flatten topics from all subjects
                            const topics = [];
                            (res.data.subjects || []).forEach(s => {
                              (s.topics || []).forEach(t => topics.push({...t, subject_name: s.name}));
                            });
                            setAudioTopics(topics);
                          } catch (e) {
                            setAudioTopics([]);
                          }
                        } else {
                          setAudioTopics([]);
                        }
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="">Seleccionar...</option>
                      {professions.map(p => (
                        <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  {newAudio.profession_id && (
                    <div>
                      <label className="text-sm text-slate-600 block mb-1">Tema específico</label>
                      <select
                        value={newAudio.topic_id}
                        onChange={(e) => setNewAudio({...newAudio, topic_id: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      >
                        <option value="">Todos los temas</option>
                        {audioTopics.map(t => (
                          <option key={t.topic_id} value={t.topic_id}>{t.name || t.short_name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">{audioTopics.length} temas disponibles</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-600 block mb-1">Nº de audios (máx 10)</label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={newAudio.num_audios}
                        onChange={(e) => setNewAudio({...newAudio, num_audios: parseInt(e.target.value) || 1})}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600 block mb-1">Duración máx (seg)</label>
                      <select
                        value={newAudio.max_duration}
                        onChange={(e) => setNewAudio({...newAudio, max_duration: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      >
                        <option value="60">1 minuto</option>
                        <option value="120">2 minutos</option>
                        <option value="180">3 minutos</option>
                        <option value="300">5 minutos</option>
                        <option value="600">10 minutos</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Custom Text */}
                <div className="space-y-4 p-4 bg-purple-50 rounded-xl">
                  <h4 className="font-medium text-purple-900">Desde Texto Personalizado</h4>
                  <div>
                    <label className="text-sm text-purple-700 block mb-1">Título del audio</label>
                    <Input
                      value={newAudio.title}
                      onChange={(e) => setNewAudio({...newAudio, title: e.target.value})}
                      placeholder="Ej: Resumen Tema 5"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-purple-700 block mb-1">Texto para convertir a audio</label>
                    <textarea
                      value={newAudio.custom_text}
                      onChange={(e) => setNewAudio({...newAudio, custom_text: e.target.value})}
                      placeholder="Escribe o pega el texto que quieres convertir a audio..."
                      className="w-full px-3 py-2 border border-purple-200 rounded-lg h-32 resize-none"
                    />
                    <p className="text-xs text-purple-500 mt-1">{newAudio.custom_text.length}/10000 caracteres</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={generateAudio}
                disabled={generatingAudio || (!newAudio.profession_id)}
                className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-6"
              >
                {generatingAudio ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
                ) : (
                  <><Volume2 className="w-4 h-4 mr-2" /> Generar Audio</>
                )}
              </Button>
            </div>

            {/* Audios List */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Audios Generados</h3>
              {audios.length === 0 ? (
                <div className="text-center py-8">
                  <Volume2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay audios generados</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Primero configura tu API Key de ElevenLabs en Configuración
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {audios.map((audio, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Play className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{audio.title || `Audio ${audio.audio_number || idx + 1}`}</p>
                          <p className="text-sm text-slate-500">
                            {audio.audio_type} · ~{audio.duration_estimate || 0}s · {audio.section}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {audio.is_published ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            Publicado
                          </span>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => publishAudio(audio.audio_id, ['top', 'premium', 'institutional'], audio.section)}
                          >
                            Publicar
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-rose-600"
                          onClick={() => deleteAudio(audio.audio_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Music className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Secciones de audio disponibles:</h4>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• <strong>Temas:</strong> Audios vinculados a temas específicos de cada OPE</li>
                    <li>• <strong>Resúmenes:</strong> Conceptos importantes y resúmenes generales</li>
                    <li>• <strong>Conceptos:</strong> Ideas clave y definiciones</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Leads y Contactos</h2>
                <p className="text-slate-600">Gestiona los contactos y solicitudes institucionales</p>
              </div>
              <Button onClick={loadLeads} variant="outline">
                Actualizar
              </Button>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-3xl font-bold text-slate-900">{leads.length}</span>
                    <p className="text-sm text-slate-600">Contactos generales</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <span className="text-3xl font-bold text-slate-900">{institutionalLeads.length}</span>
                    <p className="text-sm text-slate-600">Solicitudes institucionales</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold mb-4">Últimos contactos</h3>
              {leads.length === 0 && institutionalLeads.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay leads todavía. Los contactos aparecerán aquí.</p>
              ) : (
                <div className="space-y-4">
                  {[...institutionalLeads.map(l => ({...l, type: 'institutional'})), 
                    ...leads.map(l => ({...l, type: 'contact'}))]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice(0, 20)
                    .map((lead, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          lead.type === 'institutional' ? 'bg-emerald-100' : 'bg-blue-100'
                        }`}>
                          {lead.type === 'institutional' ? (
                            <Briefcase className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <UserCheck className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-900">
                              {lead.name || lead.contact_name || 'Sin nombre'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              lead.type === 'institutional' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {lead.type === 'institutional' ? 'Institucional' : 'Contacto'}
                            </span>
                            {lead.salesforce_synced && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                ☁️ CRM
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">{lead.email}</p>
                          {lead.profession_interest && (
                            <p className="text-sm text-slate-500">Interés: {lead.profession_interest}</p>
                          )}
                          {lead.company_name && (
                            <p className="text-sm text-slate-500">Empresa: {lead.company_name}</p>
                          )}
                          {lead.message && (
                            <p className="text-sm text-slate-600 mt-2 italic">&quot;{lead.message}&quot;</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(lead.created_at).toLocaleString('es-ES')}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Settings Tab */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Sistema</h2>
                <p className="text-slate-600">Pagos ON/OFF, administradores, usuarios especiales, Tutor IA</p>
              </div>
            </div>
            <SystemSettingsPanel token={localStorage.getItem('session_token')} />
          </div>
        )}

        {/* Email Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <EmailCampaigns />
        )}

        {/* Invoice Manager Tab */}
        {activeTab === 'invoices' && (
          <InvoiceManager />
        )}

        {/* AI Configuration Tab */}
        {activeTab === 'ai' && (
          <div className="space-y-6">
            <AIConfigPanel />
          </div>
        )}

        {/* Settings Tab - API Keys */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Configuración</h2>
                <p className="text-slate-600">Gestiona las API Keys y configuración del sistema</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Key className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">API Keys</h3>
                  <p className="text-sm text-slate-500">Configura las claves de API para los servicios externos</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* OpenAI API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    OpenAI API Key
                    <span className="text-slate-400 font-normal ml-2">Para GPT-4 y generación de preguntas</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.openai ? 'text' : 'password'}
                      value={apiKeys.openai_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, openai_api_key: e.target.value})}
                      placeholder="sk-..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('openai')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Emergent LLM Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Emergent LLM Key
                    <span className="text-slate-400 font-normal ml-2">Clave universal para OpenAI, Anthropic, Google</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.emergent ? 'text' : 'password'}
                      value={apiKeys.emergent_llm_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, emergent_llm_key: e.target.value})}
                      placeholder="ek-..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('emergent')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Resend API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Resend API Key
                    <span className="text-slate-400 font-normal ml-2">Para envío de emails (legacy)</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.resend ? 'text' : 'password'}
                      value={apiKeys.resend_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, resend_api_key: e.target.value})}
                      placeholder="re_..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('resend')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* SendGrid API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    SendGrid API Key
                    <span className="text-slate-400 font-normal ml-2">Para envío de emails (recomendado)</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.sendgrid ? 'text' : 'password'}
                      value={apiKeys.sendgrid_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, sendgrid_api_key: e.target.value})}
                      placeholder="SG...."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('sendgrid')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Obtén tu API Key en <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">sendgrid.com</a>
                  </p>
                </div>

                {/* Amazon SES Configuration */}
                <div className="col-span-2 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-amber-600" />
                    <h4 className="font-medium text-amber-900">Amazon SES (Recomendado)</h4>
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Nuevo</span>
                  </div>
                  <p className="text-sm text-amber-700 mb-4">
                    Amazon SES es más fiable y económico que SendGrid para el envío de emails. Configura tus credenciales de AWS aquí.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">AWS Access Key ID</label>
                      <div className="flex gap-2">
                        <Input
                          type={showKeys.aws_access_key ? 'text' : 'password'}
                          value={apiKeys.aws_access_key_id || ''}
                          onChange={(e) => setApiKeys({...apiKeys, aws_access_key_id: e.target.value})}
                          placeholder="AKIA..."
                          className="flex-1 font-mono"
                        />
                        <Button variant="outline" onClick={() => toggleShowKey('aws_access_key')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">AWS Secret Access Key</label>
                      <div className="flex gap-2">
                        <Input
                          type={showKeys.aws_secret_key ? 'text' : 'password'}
                          value={apiKeys.aws_secret_access_key || ''}
                          onChange={(e) => setApiKeys({...apiKeys, aws_secret_access_key: e.target.value})}
                          placeholder="Tu secret key..."
                          className="flex-1 font-mono"
                        />
                        <Button variant="outline" onClick={() => toggleShowKey('aws_secret_key')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">AWS Region</label>
                      <select
                        value={apiKeys.aws_region || 'eu-west-1'}
                        onChange={(e) => setApiKeys({...apiKeys, aws_region: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="eu-west-1">EU (Ireland) - eu-west-1</option>
                        <option value="eu-west-2">EU (London) - eu-west-2</option>
                        <option value="eu-west-3">EU (Paris) - eu-west-3</option>
                        <option value="eu-central-1">EU (Frankfurt) - eu-central-1</option>
                        <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                        <option value="us-west-2">US West (Oregon) - us-west-2</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email Remitente Verificado</label>
                      <Input
                        type="email"
                        value={apiKeys.aws_ses_from_email || ''}
                        onChange={(e) => setApiKeys({...apiKeys, aws_ses_from_email: e.target.value})}
                        placeholder="noreply@tudominio.com"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-white rounded-lg text-xs text-slate-600">
                    <p className="font-medium mb-2">Pasos para configurar Amazon SES:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Crea una cuenta en <a href="https://aws.amazon.com/ses/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">AWS SES</a></li>
                      <li>Verifica tu dominio o dirección de email</li>
                      <li>Crea un usuario IAM con permisos de SES</li>
                      <li>Copia las credenciales Access Key y Secret Key aquí</li>
                      <li>Si estás en sandbox, solicita salir para enviar a cualquier email</li>
                    </ol>
                  </div>
                </div>

                {/* Stripe API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Stripe API Key
                    <span className="text-slate-400 font-normal ml-2">Para procesamiento de pagos</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.stripe ? 'text' : 'password'}
                      value={apiKeys.stripe_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, stripe_api_key: e.target.value})}
                      placeholder="sk_..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('stripe')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* ElevenLabs API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ElevenLabs API Key
                    <span className="text-slate-400 font-normal ml-2">Para generación de audios con IA</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.elevenlabs ? 'text' : 'password'}
                      value={apiKeys.elevenlabs_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, elevenlabs_api_key: e.target.value})}
                      placeholder="xi_..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('elevenlabs')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Obtén tu API Key en <a href="https://elevenlabs.io" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">elevenlabs.io</a>
                  </p>
                </div>

                {/* ScrapingBee API Key */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ScrapingBee API Key
                    <span className="text-slate-400 font-normal ml-2">Para scraping de contenidos (multi-país)</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.scrapingbee ? 'text' : 'password'}
                      value={apiKeys.scrapingbee_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, scrapingbee_api_key: e.target.value})}
                      placeholder="Tu API Key de ScrapingBee..."
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('scrapingbee')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Obtén tu API Key en <a href="https://www.scrapingbee.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">scrapingbee.com</a> - 
                    Soporta scraping en múltiples países con proxy premium y renderizado JavaScript.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <Button 
                  onClick={saveApiKeys} 
                  disabled={savingKeys}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {savingKeys ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Guardar API Keys</>
                  )}
                </Button>
              </div>

              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Importante:</strong> Las API Keys se guardan de forma segura en el servidor. 
                  Después de guardar, reinicia el backend para que los cambios tomen efecto.
                </p>
              </div>
            </div>

            {/* Salesprocess CRM Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Salesprocess CRM</h3>
                  <p className="text-sm text-slate-500">Integración con Salesprocess para gestión de ventas</p>
                </div>
              </div>

              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg mb-4">
                <p className="text-sm text-indigo-800">
                  <strong>ℹ️ Integración Salesprocess:</strong> Conecta tu cuenta de{' '}
                  <a href="https://www.salesprocess.com" target="_blank" rel="noopener noreferrer" className="underline">
                    Salesprocess
                  </a>{' '}
                  para sincronizar leads y gestionar tu pipeline de ventas automáticamente.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Salesprocess API Key
                    <span className="text-slate-400 font-normal ml-2">Obtén tu API key desde tu panel de Salesprocess</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys.salesprocess ? 'text' : 'password'}
                      value={apiKeys.salesprocess_api_key || ''}
                      onChange={(e) => setApiKeys({...apiKeys, salesprocess_api_key: e.target.value})}
                      placeholder="sp_xxxxxxxxxxxxxxxxxxxxxxxx"
                      className="flex-1 font-mono"
                    />
                    <Button variant="outline" onClick={() => toggleShowKey('salesprocess')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Salesprocess Workspace ID
                    <span className="text-slate-400 font-normal ml-2">Identificador de tu workspace (opcional)</span>
                  </label>
                  <Input
                    value={apiKeys.salesprocess_workspace_id || ''}
                    onChange={(e) => setApiKeys({...apiKeys, salesprocess_workspace_id: e.target.value})}
                    placeholder="workspace_xxxxx"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Webhook URL (Opcional)
                    <span className="text-slate-400 font-normal ml-2">Para recibir notificaciones de Salesprocess</span>
                  </label>
                  <Input
                    value={apiKeys.salesprocess_webhook_url || ''}
                    onChange={(e) => setApiKeys({...apiKeys, salesprocess_webhook_url: e.target.value})}
                    placeholder="https://tu-dominio.com/api/webhooks/salesprocess"
                  />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <Button 
                  onClick={saveApiKeys} 
                  disabled={savingKeys}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {savingKeys ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Guardar Configuración Salesprocess</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Materials/Temarios Tab */}
        {activeTab === 'materials' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Temarios y Material</h2>
                <p className="text-slate-600">Sube documentos de referencia para cada oposición</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold">Subir Material</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Seleccionar Profesión</label>
                    <select
                      value={selectedProfessionForMaterial}
                      onChange={(e) => {
                        setSelectedProfessionForMaterial(e.target.value);
                        loadProfessionMaterials(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    >
                      <option value="">Selecciona una profesión...</option>
                      {professions.map(p => (
                        <option key={p.profession_id} value={p.profession_id}>
                          {p.name} {p.category ? `(${p.category})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Archivo</label>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.md,.zip,.pptx,.xlsx"
                        onChange={(e) => setMaterialFile(e.target.files[0])}
                        className="hidden"
                        id="material-upload"
                      />
                      <label htmlFor="material-upload" className="cursor-pointer">
                        <FolderOpen className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">
                          {materialFile ? (
                            <>
                              <span className="font-medium">{materialFile.name}</span>
                              <span className="text-slate-400 ml-2">
                                ({(materialFile.size / (1024 * 1024)).toFixed(2)} MB)
                              </span>
                            </>
                          ) : 'Haz clic para seleccionar archivo'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          PDF, DOC, DOCX, TXT, MD, ZIP, PPTX, XLSX (hasta 2GB)
                        </p>
                        <p className="text-xs text-indigo-500 mt-1">
                          💡 Los archivos ZIP se extraerán automáticamente
                        </p>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Descripción del material</label>
                    <Input
                      value={materialDescription}
                      onChange={(e) => setMaterialDescription(e.target.value)}
                      placeholder="Ej: Temario oficial BOE 2024, Legislación sanitaria..."
                    />
                  </div>

                  <Button 
                    onClick={uploadMaterial}
                    disabled={uploadingMaterial || !selectedProfessionForMaterial || !materialFile}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {uploadingMaterial ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {uploadStatus || 'Subiendo...'}</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Subir Material</>
                    )}
                  </Button>

                  {/* Upload Progress Bar */}
                  {uploadingMaterial && uploadProgress > 0 && (
                    <div className="space-y-2">
                      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-600 transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-center text-slate-600">{uploadProgress}% completado</p>
                    </div>
                  )}
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>📚 Uso del material:</strong> Los documentos subidos servirán como base de conocimiento 
                    para el Tutor IA y para la generación automática de preguntas de test.
                  </p>
                </div>
              </div>

              {/* Materials List */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Database className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold">Material Existente</h3>
                </div>

                {!selectedProfessionForMaterial ? (
                  <div className="text-center py-12 text-slate-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Selecciona una profesión para ver su material</p>
                  </div>
                ) : professionMaterials.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay material subido para esta profesión</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {professionMaterials.map((material, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-indigo-500" />
                          <div>
                            <p className="font-medium text-slate-900">{material.filename}</p>
                            <p className="text-sm text-slate-500">{material.description || 'Sin descripción'}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(material.uploaded_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-rose-600 hover:text-rose-700"
                          onClick={() => deleteMaterial(material.material_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Books Tab - KDP Book Generator */}
        {activeTab === 'books' && (
          <div className="space-y-6">
            <BookGenerator professions={professions} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
